import React, { useState, useEffect, useRef } from 'react';
import './UnifiedConsentFormModal.css';
import { getLocalDateString, localToUTCISOString, utcToLocalDateString, parseDateOnly } from '../utils/dateUtils';

interface StudentInfo {
  firstName: string;
  lastName: string;
  preferredName?: string;
  dateOfBirth: string;
  grade?: string;
  email?: string;
  phone?: string;
}

interface ParentInfo {
  fullName: string;
  relationship: 'parent' | 'guardian' | 'other';
  email: string;
  primaryPhone: string;
  secondaryPhone?: string;
  mailingAddress: {
    street: string;
    city: string;
    province: string;
    postalCode: string;
  };
}

interface EmergencyContact {
  fullName: string;
  relationship: string;
  phone: string;
  alternatePhone: string;
}

interface MedicalInfo {
  hasMedicalConditions: boolean;
  medicalConditions?: string;
  actionIfSymptoms?: string;
  hasAllergies: boolean;
  allergies?: string;
  allergySeverity?: 'mild' | 'moderate' | 'severe';
  carriesEpiPen?: boolean;
  medications?: {
    name: string;
    dosage: string;
    instructions: string;
    selfAdminister: boolean;
  }[];
  additionalHealthInfo?: string;
  provincialHealthNumber?: string;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
}

interface UnifiedConsentFormData {
  // Section 1: Student & Contact Info
  student: StudentInfo;
  primaryParent: ParentInfo;
  secondaryParent?: ParentInfo;
  emergencyContact: EmergencyContact;
  heardAboutOARA?: string[];
  
  // Section 2: Program Acknowledgment
  programAcknowledgment: boolean;
  
  // Section 3: Media Consent
  mediaConsent: 'yes' | 'no' | null;
  understandsMediaTerms: boolean;
  
  // Section 4: Privacy
  privacyConsent: boolean;
  
  // Section 5: Medical
  medical: MedicalInfo;
  emergencyMedicalAuthorization: boolean;
  
  // Section 6: Liability
  liabilityConsent: boolean;
  
  // Section 7: Code of Conduct
  codeOfConductConsent: boolean;
  
  // Section 8: Final Consent
  finalAcknowledgment: boolean;
  newsletterSubscription: boolean;
  
  // Signature
  parentSignature: string;
  parentName: string;
  signatureDate: string;
  relationship: 'parent' | 'guardian' | 'other';
  additionalGuardianSignature?: string;
  additionalGuardianName?: string;
  additionalGuardianDate?: string;
}

interface UnifiedConsentFormModalProps {
  studentInfo: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    grade?: string;
  };
  parentInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address?: string;
  };
  isOpen: boolean;
  onClose: () => void;
  onSign: (formData: UnifiedConsentFormData, signatureData: string) => Promise<any>;
  isRegistrationMode?: boolean;
}

type FormSection = 
  | 'student-info'
  | 'program-acknowledgment'
  | 'media-consent'
  | 'privacy'
  | 'medical'
  | 'liability'
  | 'code-of-conduct'
  | 'signature';

const UnifiedConsentFormModal: React.FC<UnifiedConsentFormModalProps> = ({
  studentInfo,
  parentInfo,
  isOpen,
  onClose,
  onSign,
  isRegistrationMode = false
}) => {
  const [currentSection, setCurrentSection] = useState<FormSection>('student-info');
  const [signature, setSignature] = useState('');
  const [isSigning, setIsSigning] = useState(false);
  const [isSigned, setIsSigned] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Helper function to strip country code from phone number
  const stripPhoneCountryCode = (phone: string | undefined): string => {
    if (!phone) return '';
    // Remove all non-digits
    const digitsOnly = phone.replace(/\D/g, '');
    // If starts with 1 and has 11 digits, remove the leading 1
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return digitsOnly.substring(1);
    }
    // If has 10 digits, return as is
    if (digitsOnly.length === 10) {
      return digitsOnly;
    }
    // Otherwise return last 10 digits
    return digitsOnly.slice(-10);
  };

  // Helper function to format date of birth for input field
  // IMPORTANT: Date-only fields (like DOB) should NEVER be timezone-converted
  // They are stored and displayed as-is (YYYY-MM-DD format)
  const formatDateOfBirth = (dob: string | undefined): string => {
    if (!dob) return '';
    // If already in YYYY-MM-DD format, return as is (no conversion needed)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
      return dob;
    }
    // If it's an ISO datetime string, extract just the date part
    if (dob.includes('T')) {
      return dob.split('T')[0];
    }
    // If it's a date string with timezone info, extract date part
    if (dob.includes('Z') || dob.includes('+') || dob.includes('-', 10)) {
      // Extract date part before any timezone indicators
      const datePart = dob.split(/[TZ+\-]/)[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        return datePart;
      }
    }
    // Fallback: return as-is
    return dob;
  };

  const [formData, setFormData] = useState<UnifiedConsentFormData>({
    // Section 1
    student: {
      firstName: studentInfo.firstName,
      lastName: studentInfo.lastName,
      // Format date of birth properly for input field
      dateOfBirth: formatDateOfBirth(studentInfo.dateOfBirth),
      grade: studentInfo.grade
    },
    primaryParent: {
      fullName: `${parentInfo.firstName} ${parentInfo.lastName}`,
      relationship: 'parent',
      email: parentInfo.email,
      // Strip country code from phone number for display/input
      primaryPhone: stripPhoneCountryCode(parentInfo.phone),
      mailingAddress: {
        street: parentInfo.address || '',
        city: '',
        province: 'BC',
        postalCode: ''
      }
    },
    heardAboutOARA: [],
    emergencyContact: {
      fullName: '',
      relationship: '',
      phone: '',
      alternatePhone: ''
    },
    // Section 2
    programAcknowledgment: false,
    // Section 3
    mediaConsent: null,
    understandsMediaTerms: false,
    // Section 4
    privacyConsent: false,
    // Section 5
    medical: {
      hasMedicalConditions: false,
      hasAllergies: false
    },
    emergencyMedicalAuthorization: false,
    // Section 6
    liabilityConsent: false,
    // Section 7
    codeOfConductConsent: false,
    // Section 8
    finalAcknowledgment: false,
    newsletterSubscription: false,
    // Signature
    parentSignature: '',
    parentName: '',
    signatureDate: getLocalDateString(), // Use local timezone (or PST) for UI
    relationship: 'parent'
  });

  // Update form data when studentInfo or parentInfo changes (especially dateOfBirth and phone)
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        student: {
          ...prev.student,
          dateOfBirth: formatDateOfBirth(studentInfo.dateOfBirth),
          firstName: studentInfo.firstName,
          lastName: studentInfo.lastName,
          grade: studentInfo.grade
        },
        primaryParent: {
          ...prev.primaryParent,
          fullName: `${parentInfo.firstName} ${parentInfo.lastName}`,
          email: parentInfo.email,
          primaryPhone: stripPhoneCountryCode(parentInfo.phone)
        }
      }));
    }
  }, [isOpen, studentInfo.dateOfBirth, studentInfo.firstName, studentInfo.lastName, studentInfo.grade, parentInfo.phone, parentInfo.firstName, parentInfo.lastName, parentInfo.email]);

  useEffect(() => {
    if (isOpen) {
      // Reset form when opened
      setCurrentSection('student-info');
      setSignature('');
      setError(null);
      setIsSigning(false);
      setIsSigned(false);
      // Scroll to top when modal opens
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = 0;
        }
        window.scrollTo(0, 0);
      }, 100);
    }
  }, [isOpen]);

  // Scroll to top when section changes
  useEffect(() => {
    if (isOpen && contentRef.current) {
      // Use setTimeout to ensure content is rendered before scrolling
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = 0;
        }
        // Also scroll window to top as fallback
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
    }
  }, [currentSection, isOpen]);

  const sections: FormSection[] = [
    'student-info',
    'program-acknowledgment',
    'media-consent',
    'privacy',
    'medical',
    'liability',
    'code-of-conduct',
    'signature'
  ];

  const sectionTitles: Record<FormSection, string> = {
    'student-info': '👤 Student & Contact Information',
    'program-acknowledgment': '🔬 Program Acknowledgment',
    'media-consent': '📸 Media Release & Image Consent',
    'privacy': '🔒 Privacy & Personal Information',
    'medical': '🏥 Medical Information & Emergency Authorization',
    'liability': '⚖️ Liability Waiver',
    'code-of-conduct': '📋 Code of Conduct',
    'signature': '✍️ Final Consent & Signature'
  };

  const currentSectionIndex = sections.indexOf(currentSection);

  const nextSection = () => {
    const nextIndex = currentSectionIndex + 1;
    if (nextIndex < sections.length) {
      setCurrentSection(sections[nextIndex]);
      setError(null);
      // Scroll will be handled by useEffect
    }
  };

  const prevSection = () => {
    const prevIndex = currentSectionIndex - 1;
    if (prevIndex >= 0) {
      setCurrentSection(sections[prevIndex]);
      setError(null);
      // Scroll will be handled by useEffect
    }
  };

  const goToSection = (section: FormSection) => {
    setCurrentSection(section);
    setError(null);
    // Scroll will be handled by useEffect
  };

  const updateFormData = (field: keyof UnifiedConsentFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateNestedField = (path: string[], value: any) => {
    setFormData(prev => {
      const newData = { ...prev };
      let current: any = newData;
      for (let i = 0; i < path.length - 1; i++) {
        current[path[i]] = { ...current[path[i]] };
        current = current[path[i]];
      }
      current[path[path.length - 1]] = value;
      return newData;
    });
  };

  const canProceedFromSection = (section: FormSection): boolean => {
    switch (section) {
      case 'student-info':
        return !!(
          formData.student.firstName &&
          formData.student.lastName &&
          formData.student.dateOfBirth &&
          formData.student.grade &&
          formData.primaryParent.fullName &&
          formData.primaryParent.email &&
          formData.primaryParent.primaryPhone &&
          formData.emergencyContact.fullName &&
          formData.emergencyContact.relationship &&
          formData.emergencyContact.phone
        );
      case 'program-acknowledgment':
        return formData.programAcknowledgment;
      case 'media-consent':
        // If no consent selected, cannot proceed
        if (formData.mediaConsent === null) return false;
        // If "no" is selected, can proceed (no terms to understand)
        if (formData.mediaConsent === 'no') return true;
        // If "yes" is selected, must understand terms
        return formData.understandsMediaTerms;
      case 'privacy':
        return formData.privacyConsent;
      case 'medical':
        return formData.emergencyMedicalAuthorization;
      case 'liability':
        return formData.liabilityConsent;
      case 'code-of-conduct':
        return formData.codeOfConductConsent;
      case 'signature':
        return formData.finalAcknowledgment &&
               !!signature.trim() &&
               !!formData.parentName.trim();
      default:
        return true;
    }
  };

  const handleSign = async () => {
    if (!canProceedFromSection('signature')) {
      setError('Please complete all required fields and provide your signature');
      return;
    }

    setIsSigning(true);
    setError(null);

    try {
      // Update form data with signature
      updateFormData('parentSignature', signature);
      updateFormData('parentName', formData.parentName || signature);

      // Convert dates from local/PST to UTC for database storage
      const formDataForBackend = {
        ...formData,
        signatureDate: formData.signatureDate ? localToUTCISOString(formData.signatureDate).split('T')[0] : formData.signatureDate,
        student: {
          ...formData.student,
          // Date-only fields should be sent as-is (YYYY-MM-DD) without timezone conversion
          dateOfBirth: formData.student.dateOfBirth || ''
        }
      };

      const signatureData = JSON.stringify({
        signature: signature,
        timestamp: new Date().toISOString(), // Keep timestamp in UTC
        ip: 'client-ip',
        formData: formDataForBackend // Use UTC-converted form data
      });

      const response = await onSign(formDataForBackend, signatureData);

      if (response && response.success) {
        // Show success message before closing
        setIsSigned(true);
        setIsSigning(false);
        
        // Wait a moment to show success message, then close
        setTimeout(() => {
          setIsSigned(false);
          onClose();
        }, 3000); // Show success message for 3 seconds
        
        return response;
      } else {
        setError('Failed to sign form. Please try again.');
        setIsSigning(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sign form. Please try again.');
      setIsSigning(false);
    }
  };

  const isSectionComplete = (section: FormSection): boolean => {
    return canProceedFromSection(section);
  };

  if (!isOpen) {
    return null;
  }

  // Show success screen after signing
  if (isSigned) {
    return (
      <div className="unified-consent-modal-overlay success-overlay">
        <div className="unified-consent-modal success-screen">
          <div className="success-content">
            <div className="success-icon">✓</div>
            <h2>Consent Form Signed Successfully!</h2>
            <p>Your consent form has been submitted and processed.</p>
            <p className="success-details">
              The form has been saved and you will receive a confirmation email shortly.
            </p>
            <div className="success-actions">
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setIsSigned(false);
                  onClose();
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="unified-consent-modal-overlay">
      <div className="unified-consent-modal">
        {/* Header */}
        <div className="unified-consent-header">
          <div className="header-content">
            <div className="oara-logo-section">
              <h2>OARA YOUTH INNOVATION LAB</h2>
              <p>Program Consent & Registration Form</p>
            </div>
            <div className="contact-info">
              <p>Downtown Vernon Makerspace</p>
              <p>3125 31 Ave, Vernon, BC V1T 2H1</p>
              <p>Phone: (236) 795-0470</p>
              <p>Email: oarayouthinnovationlab@gmail.com</p>
            </div>
          </div>
          {!isRegistrationMode && (
            <button className="close-button" onClick={onClose}>✕</button>
          )}
        </div>

        {/* Progress Indicator */}
        <div className="progress-indicator">
          <div className="section-tabs">
            {sections.map((section, index) => (
              <button
                key={section}
                className={`section-tab ${section === currentSection ? 'active' : ''} ${
                  isSectionComplete(section) ? 'completed' : ''
                }`}
                onClick={() => goToSection(section)}
                disabled={isSigning}
              >
                <span className="section-number">{index + 1}</span>
                {isSectionComplete(section) && !(section === currentSection) && (
                  <span className="checkmark">✓</span>
                )}
              </button>
            ))}
          </div>
          <div className="mobile-step-indicator" aria-hidden="true">
            {sections.map((section, index) => (
              <span
                key={`mobile-step-${section}`}
                className={`mobile-step-dot ${section === currentSection ? 'active' : ''} ${
                  isSectionComplete(section) ? 'completed' : ''
                }`}
              />
            ))}
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${((currentSectionIndex + 1) / sections.length) * 100}%` }}
            />
          </div>
          <p className="page-counter">Page {currentSectionIndex + 1} of {sections.length}</p>
        </div>

        {/* Form Content */}
        <div className="unified-consent-content" ref={contentRef}>
          {error && <div className="error-message">{error}</div>}

          {/* Section 1: Student & Contact Information */}
          {currentSection === 'student-info' && (
            <div className="form-section-content">
              <h3>{sectionTitles['student-info']}</h3>
              <p className="section-description">
                This information helps us keep your child safe and stay in touch with your family.
              </p>

              <div className="subsection">
                <h4>Student Information</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Student's Legal Name - First Name *</label>
                    <input
                      type="text"
                      value={formData.student.firstName}
                      onChange={(e) => updateNestedField(['student', 'firstName'], e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={formData.student.lastName}
                      onChange={(e) => updateNestedField(['student', 'lastName'], e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Preferred Name (if different)</label>
                    <input
                      type="text"
                      value={formData.student.preferredName || ''}
                      onChange={(e) => updateNestedField(['student', 'preferredName'], e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Date of Birth *</label>
                    <input
                      type="date"
                      value={formData.student.dateOfBirth || ''}
                      onChange={(e) => updateNestedField(['student', 'dateOfBirth'], e.target.value)}
                      required
                      max={getLocalDateString()} // Can't be in the future (using local timezone)
                      style={{
                        padding: '12px',
                        border: '2px solid #e1e8ed',
                        borderRadius: '6px',
                        fontSize: '0.95rem',
                        fontFamily: 'Arial, sans-serif',
                        width: '100%',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Student Grade *</label>
                    <input
                      type="text"
                      value={formData.student.grade || ''}
                      onChange={(e) => updateNestedField(['student', 'grade'], e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Student Email (if applicable)</label>
                    <input
                      type="email"
                      value={formData.student.email || ''}
                      onChange={(e) => updateNestedField(['student', 'email'], e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Student Phone (if applicable)</label>
                    <input
                      type="tel"
                      value={formData.student.phone || ''}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length > 10) value = value.slice(0, 10);
                        updateNestedField(['student', 'phone'], value);
                      }}
                      placeholder="2501234567"
                      maxLength={10}
                    />
                    <small>10-digit Canadian phone number</small>
                  </div>
                </div>
              </div>

              <div className="subsection">
                <h4>Primary Parent/Guardian Contact</h4>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      value={formData.primaryParent.fullName}
                      onChange={(e) => updateNestedField(['primaryParent', 'fullName'], e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Relationship to Student *</label>
                    <select
                      value={formData.primaryParent.relationship}
                      onChange={(e) => updateNestedField(['primaryParent', 'relationship'], e.target.value)}
                      required
                    >
                      <option value="parent">Parent</option>
                      <option value="guardian">Guardian</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label>Primary Email *</label>
                    <input
                      type="email"
                      value={formData.primaryParent.email}
                      onChange={(e) => updateNestedField(['primaryParent', 'email'], e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Primary Phone *</label>
                    <input
                      type="tel"
                      value={formData.primaryParent.primaryPhone}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length > 10) value = value.slice(0, 10);
                        updateNestedField(['primaryParent', 'primaryPhone'], value);
                      }}
                      placeholder="2501234567"
                      maxLength={10}
                      required
                    />
                    <small>10-digit Canadian phone number</small>
                  </div>
                  <div className="form-group">
                    <label>Secondary Phone</label>
                    <input
                      type="tel"
                      value={formData.primaryParent.secondaryPhone || ''}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length > 10) value = value.slice(0, 10);
                        updateNestedField(['primaryParent', 'secondaryPhone'], value);
                      }}
                      placeholder="2501234567"
                      maxLength={10}
                    />
                    <small>10-digit Canadian phone number</small>
                  </div>
                </div>
              </div>

              <div className="subsection">
                <h4>Mailing Address</h4>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Street Address</label>
                    <input
                      type="text"
                      value={formData.primaryParent.mailingAddress.street}
                      onChange={(e) => updateNestedField(['primaryParent', 'mailingAddress', 'street'], e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={formData.primaryParent.mailingAddress.city}
                      onChange={(e) => updateNestedField(['primaryParent', 'mailingAddress', 'city'], e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Province</label>
                    <input
                      type="text"
                      value={formData.primaryParent.mailingAddress.province}
                      onChange={(e) => updateNestedField(['primaryParent', 'mailingAddress', 'province'], e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Postal Code</label>
                    <input
                      type="text"
                      value={formData.primaryParent.mailingAddress.postalCode}
                      onChange={(e) => updateNestedField(['primaryParent', 'mailingAddress', 'postalCode'], e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="subsection">
                <h4>Emergency Contact</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      value={formData.emergencyContact.fullName}
                      onChange={(e) => updateFormData('emergencyContact', {
                        ...formData.emergencyContact,
                        fullName: e.target.value
                      })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Relationship to Student *</label>
                    <input
                      type="text"
                      value={formData.emergencyContact.relationship}
                      onChange={(e) => updateFormData('emergencyContact', {
                        ...formData.emergencyContact,
                        relationship: e.target.value
                      })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      value={formData.emergencyContact.phone}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length > 10) value = value.slice(0, 10);
                        updateFormData('emergencyContact', {
                          ...formData.emergencyContact,
                          phone: value
                        });
                      }}
                      placeholder="2501234567"
                      maxLength={10}
                      required
                    />
                    <small>10-digit Canadian phone number</small>
                  </div>
                  <div className="form-group">
                    <label>Alternate Phone</label>
                    <input
                      type="tel"
                      value={formData.emergencyContact.alternatePhone}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length > 10) value = value.slice(0, 10);
                        updateFormData('emergencyContact', {
                          ...formData.emergencyContact,
                          alternatePhone: value
                        });
                      }}
                      placeholder="2501234567"
                      maxLength={10}
                    />
                    <small>10-digit Canadian phone number</small>
                  </div>
                </div>
              </div>

              <div className="subsection">
                <h4>How did you hear about OARA?</h4>
                <div className="checkbox-group">
                  {['Friend/Family', 'School', 'Social Media', 'Website', 'Community Event', 'Other'].map((option) => (
                    <label key={option} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.heardAboutOARA?.includes(option) || false}
                        onChange={(e) => {
                          const current = formData.heardAboutOARA || [];
                          if (e.target.checked) {
                            updateFormData('heardAboutOARA', [...current, option]);
                          } else {
                            updateFormData('heardAboutOARA', current.filter(item => item !== option));
                          }
                        }}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Section 2: Program Acknowledgment */}
          {currentSection === 'program-acknowledgment' && (
            <div className="form-section-content">
              <h3>{sectionTitles['program-acknowledgment']}</h3>
              <p className="section-description">
                Understanding what your child will experience at OARA
              </p>

              <div className="info-box">
                <h4>About OARA Youth Innovation Lab</h4>
                <p>
                  OARA Youth Innovation Lab is a community-based STEM education program serving youth ages 6-18 in Vernon, BC. 
                  We provide hands-on learning experiences that build skills in science, technology, engineering, and mathematics 
                  while fostering creativity, problem-solving, and teamwork.
                </p>
              </div>

              <div className="info-box">
                <h4>Our Programs Include:</h4>
                <ul>
                  <li><strong>Robotics & Electronics</strong> - LEGO Spike Prime and EV3 kits, raspberry pi and esp32, circuit building</li>
                  <li><strong>Coding & Programming</strong> - Scratch, Python, web development, game design</li>
                  <li><strong>3D Design & Printing</strong> - Tinkercad and Blender, 3D printer operation (supervised)</li>
                  <li><strong>Makerspace Activities</strong> - Hand tools, electronics workbench</li>
                  <li><strong>Competitions & Showcases</strong> - Robotics competitions (FLL, FTC), coding challenges, project presentations</li>
                </ul>
              </div>

              <div className="checkbox-section">
                <h4>Program Acknowledgment</h4>
                <p>I acknowledge and consent to my child participating in OARA Youth Innovation Lab activities and understand that:</p>
                
                <h5 style={{ marginTop: '15px', marginBottom: '10px' }}>My child will:</h5>
                <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                  <li>Use computers, tablets, and electronic devices with internet access</li>
                  <li>Work with robotics equipment and programmable devices</li>
                  <li>Operate 3D printers and design software under supervision</li>
                  <li>Handle small electronic components, wires, and circuits</li>
                  <li>Use age-appropriate hand tools and fabrication equipment under supervision</li>
                  <li>Participate in group activities and collaborative projects</li>
                  <li>May participate in competitions and public showcase events</li>
                </ul>

                <h5 style={{ marginTop: '15px', marginBottom: '10px' }}>Safety & Supervision:</h5>
                <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                  <li>All activities are supervised by qualified OARA instructors and trained mentors</li>
                  <li>Comprehensive safety protocols are in place for all equipment use</li>
                  <li>Participants receive proper training before using any tools or equipment</li>
                  <li>Safety equipment (goggles, gloves, etc.) is provided and required when appropriate</li>
                  <li>Age-appropriate activities are assigned based on skill level and maturity</li>
                  <li>OARA maintains appropriate liability insurance for program activities</li>
                  <li>All staff and volunteers undergo background checks</li>
                </ul>

                <p style={{ marginBottom: '15px' }}>I agree that my child will follow all instructor guidance and safety rules, use equipment only with permission and supervision, wear required safety equipment, report any safety concerns immediately, and respect other participants and program property.</p>

                <label className="checkbox-label" style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '20px' }}>
                  <input
                    type="checkbox"
                    checked={formData.programAcknowledgment}
                    onChange={(e) => updateFormData('programAcknowledgment', e.target.checked)}
                    style={{ width: '20px', height: '20px', marginRight: '10px' }}
                  />
                  <span>I acknowledge and agree to all of the above program terms and safety measures</span>
                </label>
              </div>
            </div>
          )}

          {/* Section 3: Media Consent */}
          {currentSection === 'media-consent' && (
            <div className="form-section-content">
              <h3>{sectionTitles['media-consent']}</h3>
              <p className="section-description">
                How we may document and share your child's OARA experience
              </p>

              <div className="info-box">
                <h4>Program Documentation & Celebration</h4>
                <p>
                  To showcase our students' amazing work and celebrate their achievements, OARA may take photographs 
                  and videos during program activities. These images help us share excitement about STEM learning, 
                  attract funding, inspire others, document progress, and celebrate achievements.
                </p>
              </div>

              <div className="info-box">
                <h4>Where Images May Be Used:</h4>
                <ul>
                  <li>Social media platforms (Facebook, Instagram, Twitter, LinkedIn)</li>
                  <li>OARA website (oarayouthinnovationlab.ca)</li>
                  <li>Promotional materials (brochures, posters, flyers)</li>
                  <li>Grant applications and reports to funders</li>
                  <li>Annual reports and newsletters</li>
                  <li>Local media coverage (Vernon Morning Star, Castanet, community TV)</li>
                  <li>Educational presentations and community events</li>
                  <li>Internal program documentation and improvement</li>
                </ul>
              </div>

              <div className="radio-section">
                <h4>Your Media Consent Preference</h4>
                <p className="instruction-text">
                  We respect that families have different comfort levels with media sharing. Please select ONE option that works best for your family:
                </p>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="mediaConsent"
                      value="yes"
                      checked={formData.mediaConsent === 'yes'}
                      onChange={(e) => updateFormData('mediaConsent', 'yes')}
                    />
                    <span><strong>YES - I CONSENT</strong> to OARA photographing and recording my child and using these images for program promotion, including social media, website, marketing materials, and media coverage as described above.</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="mediaConsent"
                      value="no"
                      checked={formData.mediaConsent === 'no'}
                      onChange={(e) => updateFormData('mediaConsent', 'no')}
                    />
                    <span><strong>NO - I DO NOT CONSENT</strong> to my child being photographed or recorded during OARA activities.</span>
                  </label>
                </div>
                <p className="note-text">
                  <em>Note: If you select "No," we will make every reasonable effort to exclude your child from photos and videos, 
                  though this cannot be guaranteed in large group settings or events.</em>
                </p>
              </div>

              {formData.mediaConsent === 'yes' && (
                <div className="checkbox-section">
                  <h4>Image Usage Terms</h4>
                  <div className="checkbox-group">
                    {[
                      'No compensation or payment will be provided for use of my child\'s image',
                      'My child\'s full name will NOT be published alongside images without my additional written permission',
                      'Images will present students in a positive, safe, and educational context',
                      'I may withdraw this consent at any time by providing written notice to OARA',
                      'Images already published at the time of withdrawal may remain in previously printed materials and archived posts',
                      'I will not claim copyright or ownership of images taken by OARA staff or volunteers',
                      'In group photos and videos, it may not be possible to completely exclude my child from view',
                      'OARA will always prioritize student safety, dignity, and privacy in any media use',
                      'Images may remain online indefinitely as part of OARA\'s digital archives and social media history'
                    ].map((item, index) => (
                      <label key={index} className="checkbox-label">
                        <input type="checkbox" checked={true} disabled />
                        <span>{item}</span>
                      </label>
                    ))}
                  </div>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.understandsMediaTerms}
                      onChange={(e) => updateFormData('understandsMediaTerms', e.target.checked)}
                    />
                    <span>I understand all of the above terms.</span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Section 4: Privacy */}
          {currentSection === 'privacy' && (
            <div className="form-section-content">
              <h3>{sectionTitles['privacy']}</h3>
              <p className="section-description">
                Your rights and how we protect your family's information
              </p>

              <div className="info-box">
                <h4>Information Collection & Use</h4>
                <p>
                  OARA Youth Innovation Lab collects and manages personal information in compliance with:
                </p>
                <ul>
                  <li><strong>British Columbia's Personal Information Protection Act (PIPA)</strong></li>
                  <li><strong>Canada's Personal Information Protection and Electronic Documents Act (PIPEDA)</strong></li>
                </ul>
                <p>We take privacy seriously and are committed to protecting your family's personal information.</p>
              </div>

              <div className="info-box">
                <h4>Information We Collect:</h4>
                <ul>
                  <li>Student and parent/guardian names and contact information</li>
                  <li>Student date of birth and grade level</li>
                  <li>Emergency contact details</li>
                  <li>Medical information relevant to safe program participation</li>
                  <li>Attendance records and program enrollment data</li>
                  <li>Student work, projects, and academic achievements</li>
                  <li>Photos and videos (only with consent as indicated in Section 3)</li>
                  <li>Payment and registration information</li>
                </ul>
              </div>

              <div className="info-box">
                <h4>How We Use Your Information:</h4>
                <ul>
                  <li>Program registration and enrollment administration</li>
                  <li>Communication about schedules, events, and program updates</li>
                  <li>Emergency contact and safety purposes</li>
                  <li>Medical accommodations and allergy management</li>
                  <li>Program evaluation and continuous improvement</li>
                  <li>Grant reporting and funding applications (with identifying information removed)</li>
                  <li>Compliance with legal and regulatory requirements</li>
                </ul>
              </div>

              <div className="info-box">
                <h4>Information Sharing:</h4>
                <ul>
                  <li>Personal information will <strong>NEVER</strong> be sold or shared with third parties for marketing purposes</li>
                  <li>Information may be shared with OARA instructors, volunteers, and facility staff as needed for program operation and student safety</li>
                  <li>Emergency information may be shared with medical professionals and emergency responders if necessary</li>
                  <li>Aggregate, anonymized data (with no identifying information) may be shared in reports to funders and in program evaluation</li>
                  <li>We may be required to disclose information if required by law or court order</li>
                </ul>
              </div>

              <div className="checkbox-section">
                <h4>Your Privacy Rights</h4>
                <p>I consent to OARA collecting and using my family's personal information as described above for program administration and safety purposes.</p>
                
                <h5 style={{ marginTop: '15px', marginBottom: '10px' }}>I understand that I have the right to:</h5>
                <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                  <li>Access my child's personal information held by OARA</li>
                  <li>Request corrections to any inaccurate or incomplete information</li>
                  <li>Receive information about how OARA uses and protects personal data</li>
                  <li>Withdraw consent at any time (which may affect my child's ability to participate)</li>
                  <li>File a complaint with the BC Office of the Information and Privacy Commissioner if I have concerns</li>
                </ul>

                <label className="checkbox-label" style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '20px' }}>
                  <input
                    type="checkbox"
                    checked={formData.privacyConsent}
                    onChange={(e) => updateFormData('privacyConsent', e.target.checked)}
                    style={{ width: '20px', height: '20px', marginRight: '10px' }}
                  />
                  <span>I acknowledge and agree to all of the above privacy terms and rights</span>
                </label>
              </div>

              <div className="info-box">
                <p>
                  <strong>Data Retention Policy:</strong> Personal information will be retained for the duration of your child's program participation 
                  plus 2 years for administrative and legal purposes, then securely destroyed according to privacy best practices.
                </p>
                <p>
                  <strong>Questions about privacy?</strong> Contact us at oarayouthinnovationlab@gmail.com
                </p>
              </div>
            </div>
          )}

          {/* Section 5: Medical Information */}
          {currentSection === 'medical' && (
            <div className="form-section-content">
              <h3>{sectionTitles['medical']}</h3>
              <p className="section-description">
                Critical information to keep your child safe
              </p>

              <div className="subsection">
                <h4>Medical Conditions</h4>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="hasMedicalConditions"
                      value="no"
                      checked={!formData.medical.hasMedicalConditions}
                      onChange={(e) => updateNestedField(['medical', 'hasMedicalConditions'], false)}
                    />
                    <span>No, my child has no medical conditions requiring accommodation</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="hasMedicalConditions"
                      value="yes"
                      checked={formData.medical.hasMedicalConditions}
                      onChange={(e) => updateNestedField(['medical', 'hasMedicalConditions'], true)}
                    />
                    <span>Yes, please describe:</span>
                  </label>
                </div>
                {formData.medical.hasMedicalConditions && (
                  <div className="form-group full-width">
                    <textarea
                      rows={3}
                      value={formData.medical.medicalConditions || ''}
                      onChange={(e) => updateNestedField(['medical', 'medicalConditions'], e.target.value)}
                      placeholder="Describe medical conditions..."
                    />
                  </div>
                )}
                {formData.medical.hasMedicalConditions && (
                  <div className="form-group full-width">
                    <label>What should staff do if symptoms occur?</label>
                    <textarea
                      rows={2}
                      value={formData.medical.actionIfSymptoms || ''}
                      onChange={(e) => updateNestedField(['medical', 'actionIfSymptoms'], e.target.value)}
                      placeholder="Instructions for staff..."
                    />
                  </div>
                )}
              </div>

              <div className="subsection">
                <h4>Allergies & Sensitivities</h4>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="hasAllergies"
                      value="no"
                      checked={!formData.medical.hasAllergies}
                      onChange={(e) => updateNestedField(['medical', 'hasAllergies'], false)}
                    />
                    <span>No known allergies or sensitivities</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="hasAllergies"
                      value="yes"
                      checked={formData.medical.hasAllergies}
                      onChange={(e) => updateNestedField(['medical', 'hasAllergies'], true)}
                    />
                    <span>Yes, please describe:</span>
                  </label>
                </div>
                {formData.medical.hasAllergies && (
                  <>
                    <div className="form-group full-width">
                      <textarea
                        rows={2}
                        value={formData.medical.allergies || ''}
                        onChange={(e) => updateNestedField(['medical', 'allergies'], e.target.value)}
                        placeholder="Describe allergies..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Severity</label>
                      <select
                        value={formData.medical.allergySeverity || ''}
                        onChange={(e) => updateNestedField(['medical', 'allergySeverity'], e.target.value as any)}
                      >
                        <option value="">Select...</option>
                        <option value="mild">Mild</option>
                        <option value="moderate">Moderate</option>
                        <option value="severe">Severe/Anaphylactic</option>
                      </select>
                    </div>
                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.medical.carriesEpiPen || false}
                          onChange={(e) => updateNestedField(['medical', 'carriesEpiPen'], e.target.checked)}
                        />
                        <span>Does your child carry an EpiPen?</span>
                      </label>
                    </div>
                  </>
                )}
              </div>

              <div className="subsection">
                <h4>Emergency Medical Authorization</h4>
                <div className="checkbox-section">
                  <p><strong>I authorize</strong> OARA staff to take the following actions in the event of a medical emergency involving my child:</p>
                  
                  <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                    <li>Administer basic first aid and CPR if trained and necessary</li>
                    <li>Contact emergency medical services (911) immediately if needed</li>
                    <li>Transport my child to the nearest appropriate medical facility</li>
                    <li>Authorize emergency medical treatment if I cannot be reached immediately</li>
                    <li>Share relevant medical information from this form with emergency responders</li>
                  </ul>

                  <p style={{ marginBottom: '15px' }}>I understand that OARA staff will make every reasonable effort to contact me immediately in the event of any injury or medical emergency.</p>

                  <label className="checkbox-label" style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '20px' }}>
                    <input
                      type="checkbox"
                      checked={formData.emergencyMedicalAuthorization}
                      onChange={(e) => updateFormData('emergencyMedicalAuthorization', e.target.checked)}
                      style={{ width: '20px', height: '20px', marginRight: '10px' }}
                    />
                    <span>I acknowledge and authorize all of the above emergency medical actions</span>
                  </label>
                </div>
              </div>

              <div className="subsection">
                <h4>Health Insurance Information (optional but recommended)</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Provincial Health Number</label>
                    <input
                      type="text"
                      value={formData.medical.provincialHealthNumber || ''}
                      onChange={(e) => updateNestedField(['medical', 'provincialHealthNumber'], e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Additional Insurance Provider</label>
                    <input
                      type="text"
                      value={formData.medical.insuranceProvider || ''}
                      onChange={(e) => updateNestedField(['medical', 'insuranceProvider'], e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Policy Number</label>
                    <input
                      type="text"
                      value={formData.medical.insurancePolicyNumber || ''}
                      onChange={(e) => updateNestedField(['medical', 'insurancePolicyNumber'], e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section 6: Liability Waiver */}
          {currentSection === 'liability' && (
            <div className="form-section-content">
              <h3>{sectionTitles['liability']}</h3>
              <p className="section-description">
                Important legal information about program participation
              </p>

              <div className="info-box">
                <h4>Assumption of Risk</h4>
                <p>
                  I acknowledge and understand that participation in OARA Youth Innovation Lab programs involves inherent risks, 
                  including but not limited to:
                </p>
                <ul>
                  <li>Use of electronic equipment, computers, and robotic devices</li>
                  <li>Operation of tools and machinery (3D printers, laser cutters) under supervision</li>
                  <li>Physical activities associated with building projects and STEM activities</li>
                  <li>Interaction with other program participants</li>
                  <li>Travel to and from the program location and potential field trips</li>
                  <li>Participation in competitions and public events</li>
                </ul>
                <p>
                  While OARA takes extensive precautions to ensure student safety, I understand that accidents and injuries 
                  can occur despite best efforts.
                </p>
              </div>

              <div className="checkbox-section">
                <h4>Liability Release</h4>
                <p>
                  I agree to release, waive, discharge, indemnify, and hold harmless OARA Youth Innovation Lab, its directors, 
                  officers, employees, volunteers, instructors, partners, and the Downtown Vernon Makerspace from any and all 
                  liability, claims, demands, actions, or causes of action arising out of or related to:
                </p>
                <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                  <li>Any loss, damage, injury, or death that may occur to my child while participating in OARA programs</li>
                  <li>Any loss, damage, or theft of personal property</li>
                  <li>Any claims arising from my child's participation in activities, use of facilities, or transportation</li>
                </ul>
                <p style={{ marginBottom: '15px' }}>
                  <strong>This release does NOT apply to:</strong> Claims arising from gross negligence, willful misconduct, 
                  or intentional harm by OARA staff or volunteers.
                </p>
                <p style={{ marginBottom: '15px' }}>I understand that this waiver is binding on my heirs, executors, administrators, and assigns.</p>
                <p style={{ marginBottom: '15px' }}>I understand that OARA maintains general liability insurance for program activities, but this insurance does NOT cover personal injuries to participants, and I am responsible for ensuring my child has appropriate personal/family health insurance coverage.</p>

                <label className="checkbox-label" style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '20px' }}>
                  <input
                    type="checkbox"
                    checked={formData.liabilityConsent}
                    onChange={(e) => updateFormData('liabilityConsent', e.target.checked)}
                    style={{ width: '20px', height: '20px', marginRight: '10px' }}
                  />
                  <span>I acknowledge and agree to all of the above liability terms, risk assumptions, and insurance understandings</span>
                </label>
              </div>
            </div>
          )}

          {/* Section 7: Code of Conduct */}
          {currentSection === 'code-of-conduct' && (
            <div className="form-section-content">
              <h3>{sectionTitles['code-of-conduct']}</h3>
              <p className="section-description">
                Creating a positive learning environment for everyone
              </p>

              <div className="checkbox-section">
                <h4>Code of Conduct</h4>
                
                <h5 style={{ marginTop: '15px', marginBottom: '10px' }}>My child agrees to:</h5>
                <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                  <li>Arrive on time and attend all scheduled sessions</li>
                  <li>Treat all equipment, materials, tools, and facilities with respect and care</li>
                  <li>Work collaboratively and respectfully with peers, instructors, and mentors</li>
                  <li>Follow all safety protocols, instructions, and facility rules at all times</li>
                  <li>Use program resources responsibly and only for intended educational purposes</li>
                  <li>Communicate respectfully and constructively with all community members</li>
                  <li>Take pride in their learning, projects, and contributions to OARA</li>
                  <li>Ask for help when needed and support others in their learning</li>
                  <li>Clean up their workspace and help maintain a tidy, organized environment</li>
                  <li>Report any safety concerns, equipment problems, or conflicts to staff immediately</li>
                </ul>

                <h5 style={{ marginTop: '15px', marginBottom: '10px' }}>I agree to:</h5>
                <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                  <li>Ensure my child attends regularly and arrives on time for scheduled sessions</li>
                  <li>Pick up my child promptly at program end times (late pickup fees may apply)</li>
                  <li>Notify OARA in advance of any absences whenever possible</li>
                  <li>Communicate any concerns, questions, or changes to contact information promptly</li>
                  <li>Support my child's learning, project work, and participation</li>
                  <li>Ensure my child comes prepared with any required materials</li>
                  <li>Respect OARA staff, volunteers, and program policies</li>
                  <li>Review and discuss this Code of Conduct with my child</li>
                  <li>Ensure my child's contact information and medical information remains current</li>
                </ul>

                <h5 style={{ marginTop: '15px', marginBottom: '10px' }}>Behavioral Expectations:</h5>
                <p><strong>Participants are expected to:</strong></p>
                <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                  <li>Treat everyone with kindness, respect, and dignity</li>
                  <li>Use appropriate language (no profanity, bullying, or harassment)</li>
                  <li>Respect diversity and be inclusive of all participants</li>
                  <li>Focus on learning and avoid disruptive behavior</li>
                </ul>
                <p><strong>Unacceptable behaviors include:</strong></p>
                <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                  <li>Bullying, harassment, or discrimination of any kind</li>
                  <li>Intentional damage to equipment or property</li>
                  <li>Behavior that endangers the safety of self or others</li>
                  <li>Consistent disruption of learning activities</li>
                  <li>Refusal to follow safety rules or staff instructions</li>
                  <li>Inappropriate use of technology (accessing inappropriate content, cyberbullying)</li>
                </ul>

                <p style={{ marginBottom: '15px' }}><strong>I understand</strong> that OARA reserves the right to issue verbal warnings, contact parents/guardians about concerning behavior, implement behavior plans or temporary suspensions for repeated issues, and terminate participation without refund for serious safety violations or persistent behavioral problems that disrupt the learning environment.</p>
                <p className="note-text" style={{ marginBottom: '15px' }}>
                  <em>OARA will communicate clearly with families about any behavioral concerns, work collaboratively with families to address issues, apply disciplinary measures fairly and consistently, and make reasonable accommodations for students with documented behavioral or learning needs.</em>
                </p>

                <label className="checkbox-label" style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '20px' }}>
                  <input
                    type="checkbox"
                    checked={formData.codeOfConductConsent}
                    onChange={(e) => updateFormData('codeOfConductConsent', e.target.checked)}
                    style={{ width: '20px', height: '20px', marginRight: '10px' }}
                  />
                  <span>I acknowledge and agree to all of the above code of conduct terms, responsibilities, and disciplinary processes</span>
                </label>
              </div>
            </div>
          )}

          {/* Section 8: Final Consent & Signature */}
          {currentSection === 'signature' && (
            <div className="form-section-content">
              <h3>{sectionTitles['signature']}</h3>
              <p className="section-description">
                Your acknowledgment and authorization
              </p>

              <div className="checkbox-section">
                <h4>Final Acknowledgment</h4>
                <p>By signing below, I acknowledge and confirm that:</p>
                <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                  <li>I have read and understood all sections of this consent form (8 sections total)</li>
                  <li>I have had the opportunity to ask questions about the OARA program, policies, and this consent form</li>
                  <li>The information I provided is accurate and complete to the best of my knowledge</li>
                  <li>I voluntarily consent to my child's participation in OARA Youth Innovation Lab programs</li>
                  <li>I understand that I can withdraw consent at any time by providing written notice to OARA, which may result in my child's withdrawal from the program</li>
                  <li>I have received or will receive a copy of this completed consent form for my records</li>
                  <li>I understand that OARA may update policies and will notify families of significant changes</li>
                </ul>
                <label className="checkbox-label" style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '20px' }}>
                  <input
                    type="checkbox"
                    checked={formData.finalAcknowledgment}
                    onChange={(e) => updateFormData('finalAcknowledgment', e.target.checked)}
                    style={{ width: '20px', height: '20px', marginRight: '10px' }}
                  />
                  <span>I acknowledge and agree to all of the above statements</span>
                </label>
              </div>

              <div className="newsletter-section">
                <label className="newsletter-label">
                  <input
                    type="checkbox"
                    checked={formData.newsletterSubscription}
                    onChange={(e) => updateFormData('newsletterSubscription', e.target.checked)}
                  />
                  <span className="newsletter-text">
                    <span className="newsletter-title">Subscribe to our monthly newsletter and updates</span>
                    <span className="newsletter-description">Get updates on events, new sessions, and educational resources via email and/or SMS. You can unsubscribe from emails or reply STOP to texts anytime. Message rates may apply.</span>
                  </span>
                </label>
              </div>

              <div className="signature-section">
                <h4>Parent/Guardian Signature</h4>
                <p className="legal-text">
                  <strong>I certify under penalty of perjury that I am the parent or legal guardian of the student named in this form 
                  and have the authority to provide this consent.</strong>
                </p>
                
                <div className="form-grid">
                  <div className="form-group">
                    <label>Print Full Name *</label>
                    <input
                      type="text"
                      value={formData.parentName}
                      onChange={(e) => updateFormData('parentName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Digital Signature (Type your full name) *</label>
                    <input
                      type="text"
                      value={signature}
                      onChange={(e) => setSignature(e.target.value)}
                      placeholder="Type your full name to sign"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Date *</label>
                    <input
                      type="date"
                      value={formData.signatureDate}
                      onChange={(e) => updateFormData('signatureDate', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Relationship to Student *</label>
                    <select
                      value={formData.relationship}
                      onChange={(e) => updateFormData('relationship', e.target.value as any)}
                      required
                    >
                      <option value="parent">Parent</option>
                      <option value="guardian">Legal Guardian</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="navigation-buttons">
            <button
              className="btn btn-secondary"
              onClick={prevSection}
              disabled={currentSectionIndex === 0 || isSigning}
            >
              ← Previous
            </button>
            
            {currentSectionIndex < sections.length - 1 ? (
              <button
                className="btn btn-primary"
                onClick={nextSection}
                disabled={!canProceedFromSection(currentSection) || isSigning}
              >
                Next →
              </button>
            ) : (
              <button
                className="btn btn-primary btn-sign"
                onClick={handleSign}
                disabled={!canProceedFromSection('signature') || isSigning}
              >
                {isSigning ? 'Signing...' : '✓ Sign Consent Form'}
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="unified-consent-footer">
          <p>OARA Youth Innovation Lab | (236) 795-0470 | oarayouthinnovationlab@gmail.com</p>
          <p>3125 31 Ave, Vernon, BC V1T 2H1 | Page {currentSectionIndex + 1} of {sections.length}</p>
        </div>
      </div>
    </div>
  );
};

export default UnifiedConsentFormModal;

